// Screen Controller - Manages screen swapping and event delegation
import { store } from '../app/store.js';
import { Email } from '../domain/valueObjects.js';

export class ScreenController {
  private countdownTimerId: number | null = null;

  constructor(rootElement) {
    this.root = rootElement;
    this.currentScreenElement = null;
    this.setupEventDelegation();
  }

  swapScreen(screen) {
    const template = document.getElementById(screen.template);
    if (!template) {
      console.error(`Template ${screen.template} not found`);
      return;
    }

    // Clone template content
    const screenContent = template.content.cloneNode(true);

    // Clear current content with transition
    this.root.style.opacity = '0';

    setTimeout(() => {
      // Replace content
      this.root.replaceChildren(screenContent);
      this.currentScreenElement = this.root.querySelector('[data-screen]');

      // Bind data if screen has data field
      if (screen.dataField) {
        this.bindScreenData(screen);
      }

      // Special handling for certain screens
      this.handleSpecialScreens(screen);

      // Fade in new content
      this.root.style.opacity = '1';

      // Focus for accessibility
      this.root.focus();

      // Dispatch screen loaded event
      document.dispatchEvent(new CustomEvent('screen:loaded', {
        detail: { screen }
      }));
    }, 150); // Match with CSS transition duration
  }

  setupEventDelegation() {
    // Handle clicks on action buttons
    this.root.addEventListener('click', (event) => {
      const target = event.target.closest('[data-action]');
      if (!target) return;

      const action = target.getAttribute('data-action');
      const value = target.getAttribute('data-value');

      this.handleAction(action, value, target, event);
    });

    // Handle input changes
    this.root.addEventListener('input', (event) => {
      const target = event.target;
      if (target.hasAttribute('data-bind')) {
        const field = target.getAttribute('data-bind');
        store.set(field, target.value);

        // Special validation for email field
        if (field === 'email') {
          this.validateEmailInput(target);
        }
      }
    });
  }

  handleAction(action, value, element, event) {
    event.preventDefault();

    switch (action) {
      case 'next':
        this.validateAndProceed();
        break;

      case 'back':
        document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'back' } }));
        break;

      case 'skip':
        document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'next' } }));
        break;

      case 'select':
        this.handleSelection(value, element);
        document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'next' } }));
        break;

      case 'toggle':
        this.handleToggle(value, element);
        break;

      case 'capture-selfie':
        document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'next' } }));
        break;

      case 'capture':
        this.capturePhoto();
        break;

      case 'retake':
        this.retakePhoto();
        break;

      case 'use-photo':
        this.usePhoto();
        break;

      case 'subscribe':
        this.openCheckout();
        break;

      case 'download-app':
        this.downloadApp();
        break;

      default:
        // Dispatch custom event for other actions
        document.dispatchEvent(new CustomEvent(`action:${action}`, {
          detail: { value, element }
        }));
    }
  }

  handleSelection(value, element) {
    // Remove selected class from siblings
    const parent = element.parentElement;
    parent.querySelectorAll('.option-card').forEach(card => {
      card.classList.remove('selected');
    });

    // Add selected class
    element.classList.add('selected');

    // Store value
    const screen = this.getCurrentScreen();
    if (screen?.dataField) {
      store.set(screen.dataField, value);
    }
  }

  handleToggle(value, element) {
    // Handle exclusive options
    if (element.classList.contains('exclusive')) {
      // If selecting "none", deselect all others
      const parent = element.parentElement;
      if (!element.classList.contains('active')) {
        parent.querySelectorAll('.selectable').forEach(card => {
          card.classList.remove('active');
        });
      }
    } else {
      // Deselect "none" if selecting other options
      const parent = element.parentElement;
      const noneOption = parent.querySelector('.exclusive');
      if (noneOption && noneOption.classList.contains('active')) {
        noneOption.classList.remove('active');
      }
    }

    // Toggle active class
    element.classList.toggle('active');

    // Collect all active values
    const parent = element.parentElement;
    const activeValues = Array.from(parent.querySelectorAll('.selectable.active'))
      .map(el => el.getAttribute('data-value'));

    // Store values
    const screen = this.getCurrentScreen();
    if (screen?.dataField) {
      store.set(screen.dataField, activeValues);
    }
  }

  validateAndProceed() {
    const screen = this.getCurrentScreen();

    // Check required fields
    if (screen?.required) {
      const input = this.root.querySelector(`[data-bind="${screen.dataField}"]`);
      if (!input || !input.value) {
        this.showValidationError('This field is required');
        return;
      }

      // Email validation using domain layer
      if (screen.validation === 'email') {
        if (!Email.isValid(input.value.trim())) {
          this.showValidationError('Please enter a valid email address');
          return;
        }

        // Store the validated email as lowercase (Email value object behavior)
        store.set(screen.dataField, input.value.trim().toLowerCase());
      }
    }

    // Proceed to next screen
    document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'next' } }));
  }

  showValidationError(message) {
    const validation = this.root.querySelector('.input-validation');
    if (validation) {
      validation.textContent = message;
      validation.classList.add('error');
    }
  }

  bindScreenData(screen) {
    const storedValue = store.get(screen.dataField);
    if (!storedValue) return;

    // For text inputs
    const input = this.root.querySelector(`[data-bind="${screen.dataField}"]`);
    if (input) {
      input.value = storedValue;
    }

    // For single select options
    if (!screen.multiSelect && typeof storedValue === 'string') {
      const option = this.root.querySelector(`[data-value="${storedValue}"]`);
      if (option) {
        option.classList.add('selected');
      }
    }

    // For multi-select options
    if (screen.multiSelect && Array.isArray(storedValue)) {
      storedValue.forEach(value => {
        const option = this.root.querySelector(`[data-value="${value}"]`);
        if (option) {
          option.classList.add('active');
        }
      });
    }
  }

  handleSpecialScreens(screen) {
    switch (screen.id) {
      case 'processing':
      case 'analysis':
        this.startProgressAnimation(screen.duration || 8000);
        break;

      case 'social-proof':
        this.updateSocialProofText();
        break;

      case 'email':
        this.setupEmailValidation();
        break;

      case 'camera':
        this.initializeCamera();
        break;

      case 'results':
        this.displayResults();
        break;

      case 'paywall':
        this.initializePaywall();
        break;

      case 'success':
        this.showSuccessAnimation();
        break;
    }
  }

  startProgressAnimation(duration) {
    const stages = [
      { text: 'Analyzing your skin profile...', progress: 25 },
      { text: 'Reviewing your lifestyle factors...', progress: 50 },
      { text: 'Comparing with thousands of success stories...', progress: 75 },
      { text: 'Generating your personalized plan...', progress: 100 }
    ];

    let currentStage = 0;
    const interval = duration / stages.length;

    // Store the current screen element to check if we're still on the same screen
    const initialScreenElement = this.currentScreenElement;

    const updateStage = () => {
      // Check if we're still on the same screen
      if (this.currentScreenElement !== initialScreenElement) {
        // User navigated away, stop the animation
        return;
      }

      if (currentStage >= stages.length) {
        // Double-check we're still on the same screen before navigating
        if (this.currentScreenElement === initialScreenElement) {
          setTimeout(() => {
            // Final check before navigation
            if (this.currentScreenElement === initialScreenElement) {
              document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'next' } }));
            }
          }, 500);
        }
        return;
      }

      const stage = stages[currentStage];
      const textElement = this.root.querySelector('#processing-text, #analysis-text');
      const progressBar = this.root.querySelector('#analysis-bar');
      const progressPercent = this.root.querySelector('#progress-percentage');
      const progressRing = this.root.querySelector('#analysis-progress');

      if (textElement) textElement.textContent = stage.text;
      if (progressBar) progressBar.style.width = `${stage.progress}%`;
      if (progressPercent) progressPercent.textContent = `${stage.progress}%`;
      if (progressRing) {
        const offset = 283 - (283 * stage.progress / 100);
        progressRing.style.strokeDashoffset = offset;
      }

      currentStage++;
      setTimeout(updateStage, interval);
    };

    updateStage();
  }

  updateSocialProofText() {
    const goal = store.get('primary_goal');
    const goalText = this.root.querySelector('#user-goal-text');

    if (goalText && goal) {
      const goalMap = {
        'clear-acne': 'clear breakouts and prevent acne',
        'reduce-aging': 'reduce fine lines and wrinkles',
        'even-tone': 'even out skin tone',
        'hydrate': 'hydrate and nourish skin',
        'calm-redness': 'calm redness and sensitivity'
      };

      goalText.textContent = goalMap[goal] || 'similar goals';
    }
  }

  setupEmailValidation() {
    const emailInput = this.root.querySelector('#email-input');
    const submitButton = this.root.querySelector('#email-submit');

    if (emailInput && submitButton) {
      // Initially disable the button
      submitButton.disabled = true;
      submitButton.classList.add('btn-disabled');

      // Validate existing value if any
      if (emailInput.value) {
        this.validateEmailInput(emailInput);
      }
    }
  }

  validateEmailInput(input) {
    const submitButton = this.root.querySelector('#email-submit');
    const validation = this.root.querySelector('#email-validation');

    if (!submitButton) return;

    const value = input.value.trim();

    // Clear previous validation
    if (validation) {
      validation.textContent = '';
      validation.classList.remove('error');
    }

    if (!value) {
      // Empty email
      submitButton.disabled = true;
      submitButton.classList.add('btn-disabled');
    } else if (!Email.isValid(value)) {
      // Invalid email - using domain layer validation
      submitButton.disabled = true;
      submitButton.classList.add('btn-disabled');
      if (validation) {
        validation.textContent = 'Please enter a valid email address';
        validation.classList.add('error');
      }
    } else {
      // Valid email
      submitButton.disabled = false;
      submitButton.classList.remove('btn-disabled');
    }
  }

  async initializeCamera() {
    try {
      const video = this.root.querySelector('#camera-preview');
      if (!video) return;

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' }
      });

      video.srcObject = stream;
      store.set('cameraStream', stream);
    } catch (error) {
      console.error('Camera access denied:', error);
      alert('Camera access is required for skin analysis. Please enable camera permissions.');
    }
  }

  capturePhoto() {
    const video = this.root.querySelector('#camera-preview');
    const canvas = this.root.querySelector('#camera-canvas');

    if (!video || !canvas) return;

    const context = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    // Convert to base64
    const imageData = canvas.toDataURL('image/jpeg', 0.8);
    store.set('selfie', imageData);

    // Hide capture button, show retake/use buttons
    this.root.querySelector('#capture-btn').style.display = 'none';
    this.root.querySelector('#retake-btn').style.display = 'inline-flex';
    this.root.querySelector('#use-photo-btn').style.display = 'inline-flex';

    // Show canvas, hide video
    canvas.style.display = 'block';
    video.style.display = 'none';
  }

  retakePhoto() {
    const video = this.root.querySelector('#camera-preview');
    const canvas = this.root.querySelector('#camera-canvas');

    if (!video || !canvas) return;

    // Show video, hide canvas
    video.style.display = 'block';
    canvas.style.display = 'none';

    // Show capture button, hide retake/use buttons
    this.root.querySelector('#capture-btn').style.display = 'inline-flex';
    this.root.querySelector('#retake-btn').style.display = 'none';
    this.root.querySelector('#use-photo-btn').style.display = 'none';
  }

  usePhoto() {
    // Stop camera stream
    const stream = store.get('cameraStream');
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }

    // Navigate to next screen
    document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'next' } }));
  }

  displayResults() {
    // Display user selfie
    const selfie = store.get('selfie');
    const userSelfieImg = this.root.querySelector('#user-selfie');
    const potentialSelfieImg = this.root.querySelector('#potential-selfie');

    if (userSelfieImg && selfie) {
      userSelfieImg.src = selfie;
      // For demo, use the same image with a filter for "potential"
      potentialSelfieImg.src = selfie;
      potentialSelfieImg.style.filter = 'brightness(1.1) contrast(0.95)';
    }
  }

  async initializePaywall() {
    // Import paywall service and config dynamically
    try {
      const [serviceModule, configModule] = await Promise.all([
        import('../domain/paywall-service.js'),
        import('../config/paywall-config.js')
      ]);

      const { paywallService } = serviceModule;
      const { PAYWALL_CONFIG } = configModule;

      // Initially show loading state
      this.renderPricingOptions(null, true);
      this.updateButtonState(true); // Disable button while loading

      // Listen for loading state changes
      paywallService.on('prices:loading', (isLoading) => {
        if (isLoading) {
          this.renderPricingOptions(null, true);
          this.updateButtonState(true);
        }
      });

      paywallService.on('prices:loaded', () => {
        const viewData = paywallService.getProcessedView();
        if (viewData) {
          this.renderPricingOptions(viewData, false);
          this.updateButtonState(false);
          // Set up option selection handlers after prices are loaded
          this.setupOptionSelection(paywallService);
        }
      });

      // Initialize paywall service
      await paywallService.initializePaywall();

      // Get processed view data
      const viewData = paywallService.getProcessedView();
      if (!viewData) {
        console.error('[ScreenController] No paywall view data available');
        this.startCountdownTimer(900); // Fallback
        this.updateButtonState(false); // Enable button even if no data
        return;
      }

      // Update voucher card
      this.updateVoucherCard(viewData);

      // If prices are already loaded, render them
      if (!paywallService.isLoading()) {
        this.renderPricingOptions(viewData, false);
        this.updateButtonState(false);
        // Set up option selection handlers
        this.setupOptionSelection(paywallService);
      }

      // Start countdown timer
      this.startCountdownTimer(PAYWALL_CONFIG.timerDuration || 600);

      // Set up checkout button handler
      this.setupCheckoutButton(paywallService);

      // Listen for view switch events
      paywallService.on('view:switched', (newView) => {
        const updatedViewData = paywallService.getProcessedView();
        if (updatedViewData) {
          this.updateVoucherCard(updatedViewData);
          this.renderPricingOptions(updatedViewData, paywallService.isLoading());
          // Restart countdown timer when switching to extra discount view
          if (newView === 'extra_discount') {
            this.startCountdownTimer(PAYWALL_CONFIG.timerDuration || 600);
          }
        }
      });

    } catch (error) {
      console.error('Failed to initialize paywall:', error);
      // Use default timer
      this.startCountdownTimer(900);
      this.updateButtonState(false); // Enable button on error
    }
  }

  startCountdownTimer(seconds) {
    // Cancel any existing timer
    if (this.countdownTimerId !== null) {
      clearTimeout(this.countdownTimerId);
      this.countdownTimerId = null;
    }

    const timerElement = this.root.querySelector('#countdown-timer');
    const voucherTimerElement = this.root.querySelector('#voucher-timer');
    const timerLabel = this.root.querySelector('.timer-label');
    const voucherExpires = this.root.querySelector('.voucher-expires');

    // Reset timer display state
    if (timerElement) {
      timerElement.style.display = '';
    }
    if (voucherTimerElement) {
      voucherTimerElement.style.display = '';
    }
    if (timerLabel) {
      timerLabel.textContent = "Your exclusive discount expires in:";
    }
    if (voucherExpires) {
      voucherExpires.textContent = "Promo code expires in:";
    }

    let remaining = seconds;

    const updateTimer = () => {
      const minutes = Math.floor(remaining / 60);
      const secs = remaining % 60;
      const timeText = `${minutes}:${secs.toString().padStart(2, '0')}`;

      if (remaining > 0) {
        if (timerElement) {
          timerElement.textContent = timeText;
        }
        if (voucherTimerElement) {
          voucherTimerElement.textContent = timeText;
        }
        remaining--;
        this.countdownTimerId = window.setTimeout(updateTimer, 1000);
      } else {
        // Timer expired - update UI
        if (timerLabel) {
          timerLabel.textContent = "No pressure, enjoy the discount";
        }
        if (voucherExpires) {
          voucherExpires.textContent = "No pressure, enjoy the discount";
        }
        // Hide the countdown timer element
        if (timerElement) {
          timerElement.style.display = 'none';
        }
        // Hide the voucher timer element
        if (voucherTimerElement) {
          voucherTimerElement.style.display = 'none';
        }

        this.countdownTimerId = null;
        document.dispatchEvent(new CustomEvent('timer:expired'));
      }
    };

    updateTimer();
  }

  openCheckout() {
    document.dispatchEvent(new CustomEvent('checkout:open'));
  }

  updateVoucherCard(viewData) {
    const voucherCode = this.root.querySelector('#voucher-code');
    const voucherDiscount = this.root.querySelector('#voucher-discount');
    const discountBadge = this.root.querySelector('#discount-badge');

    if (voucherCode) {
      voucherCode.textContent = viewData.voucherCode;
    }
    if (voucherDiscount) {
      voucherDiscount.textContent = `${viewData.discountPercent}% OFF`;
    }
    if (discountBadge) {
      discountBadge.textContent = `SAVE ${viewData.discountPercent}% TODAY`;
    }
  }

  renderPricingOptions(viewData, isLoading = false) {
    const container = this.root.querySelector('#pricing-options');
    if (!container) return;

    // Get the pricing option template
    const template = document.getElementById('pricing-option-template') as HTMLTemplateElement;
    if (!template) {
      console.error('Pricing option template not found');
      return;
    }

    // Clear existing options
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    // If loading, show placeholders
    if (isLoading) {
      // Show 3 loading placeholders
      for (let i = 0; i < 3; i++) {
        const placeholder = document.createElement('div');
        placeholder.className = 'pricing-option loading-placeholder';
        placeholder.innerHTML = `
          <div class="pricing-option-content">
            <div class="pricing-option-left">
              <div class="pricing-option-checkbox skeleton"></div>
              <div class="pricing-option-info">
                <div class="pricing-option-title skeleton-text"></div>
                <div class="pricing-option-prices">
                  <span class="pricing-crossed skeleton-text"></span>
                  <span class="pricing-current skeleton-text"></span>
                </div>
              </div>
            </div>
            <div class="pricing-option-right">
              <div class="pricing-daily-amount skeleton-text"></div>
              <div class="pricing-daily-label skeleton-text"></div>
            </div>
          </div>
        `;
        container.appendChild(placeholder);
      }
      return;
    }

    // Render each option
    let selectedIndex = 0; // Track which option should be selected

    // Find option with badge, if any
    const badgeIndex = viewData.options.findIndex(opt => opt.badge);
    if (badgeIndex !== -1) {
      selectedIndex = badgeIndex;
    }

    viewData.options.forEach((option, index) => {
      // Clone the template
      const optionEl = template.content.cloneNode(true) as DocumentFragment;
      const optionDiv = optionEl.querySelector('.pricing-option') as HTMLElement;

      if (!optionDiv) return;

      // Set the option ID
      optionDiv.dataset.optionId = option.priceId;

      // Select the determined option
      if (index === selectedIndex) {
        optionDiv.classList.add('selected');
      }

      // Set the badge if present
      const badgeEl = optionDiv.querySelector('.pricing-option-badge') as HTMLElement;
      if (option.badge && badgeEl) {
        badgeEl.textContent = option.badge;
        badgeEl.style.display = '';
      }

      // Set the title
      const titleEl = optionDiv.querySelector('.pricing-option-title');
      if (titleEl) {
        titleEl.textContent = option.title;
      }

      // Set the prices
      const crossedPriceEl = optionDiv.querySelector('.pricing-crossed');
      if (crossedPriceEl) {
        crossedPriceEl.textContent = option.crossedPrice.formatted;
      }

      const currentPriceEl = optionDiv.querySelector('.pricing-current');
      if (currentPriceEl) {
        currentPriceEl.textContent = option.uncrossedPrice.formatted;
      }

      // Set the daily price
      const dailyPriceEl = optionDiv.querySelector('.pricing-daily-amount');
      if (dailyPriceEl) {
        dailyPriceEl.textContent = option.dailyPrice.formatted;
      }

      container.appendChild(optionEl);
    });
  }

  setupOptionSelection(paywallService) {
    const container = this.root.querySelector('#pricing-options');
    if (!container) return;

    container.addEventListener('click', (e) => {
      const option = e.target.closest('.pricing-option');
      if (!option) return;

      // Remove selected from all options
      container.querySelectorAll('.pricing-option').forEach(opt => {
        opt.classList.remove('selected');
      });

      // Add selected to clicked option
      option.classList.add('selected');

      // Update service
      const optionId = option.dataset.optionId;
      paywallService.selectOption(optionId);

      // Update CTA button text with price
      const selectedOption = paywallService.getSelectedOption();
    });

    // Select default option
    const firstSelected = container.querySelector('.pricing-option.selected');
    if (firstSelected) {
      paywallService.selectOption(firstSelected.dataset.optionId);
      const selectedOption = paywallService.getSelectedOption();
    }
  }

  updateButtonState(isLoading) {
    const ctaButton = this.root.querySelector('#paywall-cta-button');
    if (!ctaButton) return;

    if (isLoading) {
      ctaButton.disabled = true;
      ctaButton.classList.add('loading');
      ctaButton.innerHTML = '<span class="loading-spinner"></span> Loading prices...';
    } else {
      ctaButton.disabled = false;
      ctaButton.classList.remove('loading');
      ctaButton.textContent = 'Get My Plan';
    }
  }

  setupCheckoutButton(paywallService) {
    const ctaButton = this.root.querySelector('#paywall-cta-button');
    if (!ctaButton) return;

    ctaButton.addEventListener('click', async () => {
      // Don't proceed if button is disabled
      if (ctaButton.disabled) return;

      try {
        // Get user email from store
        let email = store.get('email');

        // If no email, show popup to collect it
        if (!email || !Email.isValid(email)) {
          email = await this.showEmailPopup();
          if (!email) {
            // User cancelled the popup
            return;
          }
        }

        // Open checkout with email
        await paywallService.openCheckout(email);
      } catch (error) {
        console.error('[ScreenController] Failed to open checkout:', error);
      }
    });
  }

  showEmailPopup() {
    return new Promise((resolve) => {
      // Get the email popup template
      const template = document.getElementById('email-popup-template');
      if (!template) {
        console.error('Email popup template not found');
        resolve(null);
        return;
      }

      // Clone and append the popup to the body
      const popupContent = template.content.cloneNode(true);
      document.body.appendChild(popupContent);

      // Get references to popup elements
      const backdrop = document.getElementById('email-modal-backdrop');
      const closeButton = document.getElementById('email-modal-close');
      const form = document.getElementById('email-popup-form');
      const emailInput = document.getElementById('popup-email-input');
      const submitButton = document.getElementById('popup-email-submit');
      const validation = document.getElementById('popup-email-validation');

      if (!backdrop || !form || !emailInput || !submitButton) {
        console.error('Email popup elements not found');
        resolve(null);
        return;
      }

      // Function to close the popup
      const closePopup = (email = null) => {
        if (backdrop && backdrop.parentNode) {
          backdrop.parentNode.removeChild(backdrop);
        }
        resolve(email);
      };

      // Close button handler
      if (closeButton) {
        closeButton.addEventListener('click', () => closePopup());
      }

      // Click outside to close
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) {
          closePopup();
        }
      });

      // Email validation on input
      emailInput.addEventListener('input', () => {
        const value = emailInput.value.trim();

        // Clear previous validation
        if (validation) {
          validation.textContent = '';
          validation.classList.remove('error');
        }

        if (value && !Email.isValid(value)) {
          submitButton.disabled = true;
          submitButton.classList.add('btn-disabled');
          if (validation) {
            validation.textContent = 'Please enter a valid email address';
            validation.classList.add('error');
          }
        } else if (value) {
          submitButton.disabled = false;
          submitButton.classList.remove('btn-disabled');
        } else {
          submitButton.disabled = true;
          submitButton.classList.add('btn-disabled');
        }
      });

      // Initialize button state
      submitButton.disabled = true;
      submitButton.classList.add('btn-disabled');

      // Form submission
      form.addEventListener('submit', (e) => {
        e.preventDefault();

        const email = emailInput.value.trim().toLowerCase();

        if (Email.isValid(email)) {
          // Store the email
          store.set('email', email);

          // Close popup and return email
          closePopup(email);
        } else {
          if (validation) {
            validation.textContent = 'Please enter a valid email address';
            validation.classList.add('error');
          }
        }
      });

      // Focus on the input field
      emailInput.focus();
    });
  }

  downloadApp() {
    // iOS app store link
    window.location.href = 'https://apps.apple.com/app/lunai-care/id123456789'; // Replace with actual app ID
  }

  showSuccessAnimation() {
    // Create confetti effect
    const confettiContainer = this.root.querySelector('#confetti');
    if (confettiContainer) {
      for (let i = 0; i < 50; i++) {
        const confetti = document.createElement('div');
        confetti.className = 'confetti-piece';
        confetti.style.left = `${Math.random() * 100}%`;
        confetti.style.animationDelay = `${Math.random() * 3}s`;
        confetti.style.backgroundColor = ['#C33C54', '#FFB140', '#3F88C5'][Math.floor(Math.random() * 3)];
        confettiContainer.appendChild(confetti);
      }
    }

    // Display user email
    const emailDisplay = this.root.querySelector('#user-email-display');
    const userEmail = store.get('email');
    if (emailDisplay && userEmail) {
      emailDisplay.textContent = userEmail;
    }
  }

  getCurrentScreen() {
    // Get screen info from router
    const event = new CustomEvent('router:getCurrentScreen');
    document.dispatchEvent(event);
    return event.detail?.screen;
  }
}